<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="items"
      class="elevation-0"
      :options.sync="options"
      :server-items-length="totalItems"
      :loading="loading"
      :multi-sort="true"
      item-class="properties"
      :dense="false"
      :key="tableKey"
      :footer-props="footerProps"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-spacer></v-spacer>
          <vue-json-to-csv :json-data="items" :csv-title="'Subscriptions'">
            <v-btn color="primary" class="white--text font-weight-bold text-capitalize">
              Download <i class="mdi mdi-download" aria-hidden="true"></i>
            </v-btn>
          </vue-json-to-csv>
        </v-toolbar>
      </template>
      <template v-slot:item="{ item }">
        <tr :data-id="item.id">
          <td
            v-for="(header, index) in headers"
            :key="item.id + '_' + index"
            :id="
              checkLine
                ? getDescendantProp(item, checkLine) && header.value === 'id'
                  ? 'verified'
                  : header.value === 'id'
                  ? 'non-verified'
                  : ''
                : ''
            "
          >
            <span v-if="header.type === 'Date'">{{
              formatDate(getDescendantProp(item, header.value))
            }}</span>
            <span v-else-if="header.type === 'DateTime'">{{
              formatDateTime(getDescendantProp(item, header.value))
            }}</span>
            <span v-else-if="header.type === 'DateTimeLocale'">{{
              formatDateTimeLocale(getDescendantProp(item, header.value))
            }}</span>
            <span v-else-if="header.type == 'Enum'">{{
              $t(getDescendantProp(item, header.value + '.id'))
            }}</span>
            <span v-else-if="header.type == 'List'">{{
              getDescendantProp(item, header.value)
                .map(function(item) {
                  return item[header.property];
                })
                .join(', ')
            }}</span>
            <span v-else-if="header.type == 'EnumEntity'">{{
              $t(getDescendantProp(item, header.value + '.description'))
            }}</span>
            <v-layout justify-end v-else-if="header.value == 'actions' && checkUser(item)">
              <v-btn
                v-for="(action, index) in actions"
                :key="item.id + '_' + index + 'a'"
                depressed
                rounded
                outlined
                class="font-weight-bold text-capitalize"
                color="black"
                @click="onActionClick(action, item)"
              >
                {{ $t(action.text) }}
              </v-btn>
              <v-tooltip top v-for="(link, index) in links" :key="item.id + '_' + index + 'l'">
                <template v-slot:activator="{ on }" v-if="link.icon">
                  <v-icon
                    v-on="on"
                    v-if="
                      !link.attribute ||
                        link.attribute.value == getDescendantProp(item, link.attribute.id) ||
                        (link.attribute.type &&
                          link.attribute.type == 'List' &&
                          link.attribute.value.includes(getDescendantProp(item, link.attribute.id)))
                    "
                    class="mr-2"
                    @click="onLinkClick(link, item)"
                    >{{ link.icon }}
                  </v-icon>
                </template>
                <template v-slot:activator="{ on }" v-else>
                  <v-btn
                    text
                    v-on="on"
                    v-if="
                      !link.attribute ||
                        link.attribute.value == getDescendantProp(item, link.attribute.id) ||
                        link.attribute.value.includes(getDescendantProp(item, link.attribute.id))
                    "
                    class="mr-2"
                    @click="onLinkClick(link, item)"
                    >{{ $t(link.text) }}
                  </v-btn>
                </template>
                <span>{{ $t(link.text) }}</span>
              </v-tooltip>
              <v-btn
                v-if="!hideDelete"
                depressed
                rounded
                outlined
                class="font-weight-bold text-capitalize"
                color="black"
                @click="(itemToBeDeleted = item), (deleteDialog = true)"
              >
                {{ $t(deleteLabel) }}
              </v-btn>
              <v-btn
                v-if="viewRouter"
                depressed
                rounded
                class="ml-2 white--text font-weight-bold text-capitalize"
                color="primary200"
                :to="{ name: viewRouter, params: { id: item.id } }"
              >
                Overview
              </v-btn>
              <v-tooltip top v-if="editRouter">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" @click="onEditItem(item)" class="mr-2">
                    mdi-file-document-edit-outline
                  </v-icon>
                </template>
                <span>{{ $t('edit') }}</span>
              </v-tooltip>
            </v-layout>
            <span v-else>{{ getDescendantProp(item, header.value) }}</span>
          </td>
        </tr>
      </template>
      <template v-if="totalItems === 2147483647" #footer.page-text="props">
        {{ props.pageStart }} - {{ props.pageStop }}
      </template>
    </v-data-table>

    <v-dialog v-if="itemToBeDeleted !== null" v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">{{ $t(deleteLabel) }}</v-card-title>
        <v-card-text>
          {{ $t('delete.confirm') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click="deleteDialog = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn color="red darken-1" text @click="onDeleteItem" :disabled="disableDelete">
            {{ $t(deleteLabel) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import VueJsonToCsv from 'vue-json-to-csv';

export default {
  name: 'Table',
  mixins: [util],
  components: { VueJsonToCsv },
  props: {
    headers: Array,
    urlApi: String,
    checkLine: String,
    /*
    if you do not want to use generic urlApi, use urlApiDelete
     */
    urlApiDelete: String,
    deleteLabel: {
      type: String,
      default: 'delete.message',
    },
    search: Object,
    searchAttributes: Array,
    editRouter: String,
    viewRouter: String,
    hideDelete: {
      type: Boolean,
      default: false,
    },
    links: {
      type: Array,
      default: () => [],
    },
    actions: {
      type: Array,
      default: () => [],
    },
    pageList: {
      type: Boolean,
      default: false,
    },
    externalQuery: {
      type: Object,
      default: () => {},
    },
    checkOwner: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    firstLoad: true,
    totalItems: 0,
    items: [],
    loading: true,
    options: {},
    itemToBeDeleted: null,
    deleteDialog: false,
    disableDelete: false,
    tableKey: 0,
    footerProps: { 'items-per-page-options': [10, 15, 25, 50, 100] },
  }),
  watch: {
    options: {
      handler() {
        console.log('[Table] Options watch');
        if (!this.firstLoad) {
          this.changeOptions();
        }
      },
      deep: true,
    },
  },
  mounted() {
    console.log('[Table] Mounted');
    if (this.firstLoad) {
      /* Populate table from route */
      this.options.page = this.$route.query.index ? Number(this.$route.query.index) + 1 : 1;
      this.options.itemsPerPage = this.$route.query.size ? Number(this.$route.query.size) : 10;
      this.options.sortBy = [];
      this.options.sortDesc = [];
      if (this.$route.query.sort) {
        const sortList = this.$route.query.sort.split(',');
        for (let i = 0; i < sortList.length; i += 1) {
          this.options.sortBy.push(sortList[i].split(' ')[0]);
          if (sortList[i].split(' ')[1] === 'asc') {
            this.options.sortDesc.push(false);
          } else {
            this.options.sortDesc.push(true);
          }
        }
      }
      for (let i = 0; i < this.searchAttributes.length; i += 1) {
        const attr = this.searchAttributes[i];
        if (this.$route.query[attr]) {
          this.$set(this.search, attr, this.$route.query[attr]);
        }
      }
    }
    this.getDataFromApi().then(data => {
      this.items = JSON.parse(JSON.stringify(data.items));
      this.totalItems = data.total;
      this.firstLoad = false;
    });
  },
  methods: {
    triggerSearch() {
      console.log('[Table] Search watch');
      if (!this.firstLoad) {
        this.options.page = 1;
        this.changeOptions();
      }
    },
    changeOptions() {
      console.log('change options');
      this.$router
        .push({
          path: this.$router.path,
          query: this.getParams(),
        })
        .catch(err => {
          throw new Error(`Problem handling something: ${err}.`);
        });
      let currentPathName = this.$router.currentRoute.name;
      localStorage[currentPathName] = this.$router.currentRoute.fullPath;
    },
    getDataFromApi() {
      console.log('[Table] Loading data');
      this.loading = true;
      EventBus.$emit('waiting', true);
      return new Promise(resolve => {
        this.axios
          .get(`${this.urlApi}?`, {
            params: this.getParams(),
          })
          .then(res => {
            let items = [];
            let total = 0;
            if (res.status === 200) {
              items = res.data.content;
              total = res.data.total;
            }
            setTimeout(() => {
              this.loading = false;
              resolve({
                items,
                total,
              });
            });
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      });
    },
    getParams() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      let query = {};
      if (!this.pageList) {
        query = {
          index: page - 1,
          size: itemsPerPage,
        };
      }
      let sort = '';
      for (let i = 0; i < sortBy.length; i += 1) {
        if (i !== 0) {
          sort += ',';
        }
        let dir = 'asc';
        if (sortDesc[i]) {
          dir = 'desc';
        }
        sort += `${sortBy[i]} ${dir}`;
      }
      if (sort !== '') {
        query.sort = sort;
      }
      for (let i = 0; i < this.searchAttributes.length; i += 1) {
        const attr = this.searchAttributes[i];
        if (this.search[attr]) {
          query[attr] = this.search[attr];
        }
      }
      // merge with external query
      let finalQuery = {
        ...query,
        ...this.externalQuery,
      };
      return finalQuery;
    },
    onViewItem(value) {
      console.log('Click View ' + value.id);
      this.$router.push({
        name: this.viewRouter,
        params: { id: value.id },
      });
    },
    onEditItem(value) {
      console.log('Click Edit ' + value.id);
      this.$router.push({
        name: this.editRouter,
        query: { id: value.id },
      });
    },
    onDeleteItem() {
      this.disableDelete = true;
      let value = this.itemToBeDeleted;
      console.log('Click Delete ' + value.id);
      EventBus.$emit('waiting', true);
      this.axios
        .delete((this.urlApiDelete ? this.urlApiDelete : this.urlApi) + '/' + value.id)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('delete.success'),
          });
          this.deleteDialog = false;
          this.$emit('delete', value);
          this.disableDelete = false;
        })
        .catch(error => {
          console.log(error);
          this.disableDelete = false;
        })
        .finally(EventBus.$emit('waiting', false));
    },
    onLinkClick(link, item) {
      if (link.path) {
        console.log('Click Link ' + link.path);
        this.$router.push({
          name: link.path,
          params: { id: item.id },
        });
      } else {
        console.log('Click Link ' + link.pathLink);
        this.$router.push({
          path: link.pathLink + item.id,
        });
      }
    },
    onActionClick(action, item) {
      console.log('[Table] Click action ' + action.method);
      this.$emit(action.method, item);
    },
    forceTableRerender() {
      this.tableKey += 1;
    },
    checkUser(event) {
      if (this.checkOwner) {
        let userId = localStorage.getItem('userid');
        return !(userId == event.id);
      }
      return true;
    },
  },
};
</script>
